// ---------------------------------------------------------------------------
// SQLite database powered by sql.js (SQLite compiled to WebAssembly).
//
// Persistence:
//  - Desktop (Tauri): the exported bytes are written to a real .sqlite file in
//    the app-data directory via the Rust `db_save` command (plus dated backups
//    on exit). Debug and release builds use different file names (see lib.rs)
//    so dev/test data can never leak into a release install.
//  - Web: bytes go to IndexedDB, and we request `navigator.storage.persist()`
//    so the browser won't evict months of prep data under storage pressure.
//
// Durability:
//  - writes are debounced (250 ms) but flushed on beforeunload/visibility-hidden,
//    and the desktop build intercepts window close to flush + backup first.
//  - imports are validated (parse + schema-version check + migrations) BEFORE
//    the live database is replaced, so a bad file can't brick the app.
// ---------------------------------------------------------------------------

import initSqlJs, { type Database as SqlJsDatabase, type SqlValue } from 'sql.js';
// Vite serves the wasm file as an asset URL.
import wasmUrl from 'sql.js/dist/sql-wasm.wasm?url';
import { SCHEMA_SQL, SCHEMA_VERSION } from './schema';
import { getSchemaVersion, runMigrations } from './migrations';
import { notifyDbChanged } from './events';
import { invoke, isTauri } from '../ai/tauri';

const IDB_NAME = 'dental-interview-coach';
const IDB_STORE = 'sqlite';
const IDB_KEY = 'db';

let dbInstance: SqlJsDatabase | null = null;
let persistTimer: ReturnType<typeof setTimeout> | null = null;
let sqlModule: Awaited<ReturnType<typeof initSqlJs>> | null = null;

async function getSqlModule() {
  if (!sqlModule) sqlModule = await initSqlJs({ locateFile: () => wasmUrl });
  return sqlModule;
}

// --- base64 (Tauri IPC transports the DB bytes as base64 strings) -----------

function u8ToB64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function b64ToU8(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

// --- IndexedDB helpers (web persistence + desktop legacy migration) ---------

function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(): Promise<Uint8Array | null> {
  try {
    const idb = await openIdb();
    return await new Promise((resolve, reject) => {
      const tx = idb.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
      req.onsuccess = () => resolve((req.result as Uint8Array) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function idbSet(bytes: Uint8Array): Promise<void> {
  const idb = await openIdb();
  await new Promise<void>((resolve, reject) => {
    const tx = idb.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(bytes, IDB_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbClear(): Promise<void> {
  const idb = await openIdb();
  await new Promise<void>((resolve, reject) => {
    const tx = idb.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).delete(IDB_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// --- Storage backend (desktop file vs browser IndexedDB) --------------------

interface StorageBackend {
  /** Human description for the Settings screen. */
  label: string;
  load(): Promise<Uint8Array | null>;
  save(bytes: Uint8Array): Promise<void>;
  clear(): Promise<void>;
}

const idbBackend: StorageBackend = {
  label: "your browser's IndexedDB",
  load: idbGet,
  save: idbSet,
  clear: idbClear,
};

const tauriFileBackend: StorageBackend = {
  label: 'a .sqlite file in the app data folder',
  async load() {
    const b64 = await invoke<string | null>('db_load');
    return b64 ? b64ToU8(b64) : null;
  },
  async save(bytes) {
    await invoke('db_save', { dataB64: u8ToB64(bytes) });
  },
  async clear() {
    await invoke('db_clear');
  },
};

function backend(): StorageBackend {
  return isTauri() ? tauriFileBackend : idbBackend;
}

/** Where the data physically lives — surfaced in the Settings screen. */
export function storageDescription(): string {
  return backend().label;
}

// --- Lifecycle ---------------------------------------------------------------

let lifecycleWired = false;

function wireLifecyclePersistence(): void {
  if (lifecycleWired || typeof window === 'undefined') return;
  lifecycleWired = true;

  // Best-effort flush when the tab/window goes away. IndexedDB transactions
  // started here usually complete; the desktop build additionally intercepts
  // close in Rust (see below) for a guaranteed flush.
  window.addEventListener('beforeunload', () => {
    void persistNow();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void persistNow();
  });

  if (isTauri()) {
    // Rust prevents the close, emits this event, and waits for app_exit —
    // guaranteeing the last write and a dated backup land on disk.
    void import('../ai/tauri').then(({ listen }) =>
      listen('app:close-requested', async () => {
        try {
          await persistNow();
          await backupNow();
        } finally {
          await invoke('app_exit').catch(() => {});
        }
      }),
    );
  }
}

export async function initDatabase(): Promise<SqlJsDatabase> {
  if (dbInstance) return dbInstance;
  const SQL = await getSqlModule();

  // Desktop reads ONLY the .sqlite file. There is deliberately no fallback to
  // the webview's IndexedDB here: dev and release builds share the WebView2
  // profile, so such a fallback would resurrect development test data inside a
  // fresh release install. (No shipped build ever stored desktop data in
  // IndexedDB, so there is nothing to migrate.)
  const existing = await backend().load();

  const db = existing ? new SQL.Database(existing) : new SQL.Database();
  db.run(SCHEMA_SQL);
  runMigrations(db); // fresh DBs get stamped with SCHEMA_VERSION; old ones walk forward

  dbInstance = db;
  if (!existing) await persistNow();

  // Ask the browser not to evict our storage (no-op / denied is fine — it's
  // best-effort, and the desktop build uses a real file anyway).
  if (!isTauri()) {
    void navigator.storage?.persist?.().catch(() => {});
  }
  wireLifecyclePersistence();
  return db;
}

export function getDb(): SqlJsDatabase {
  if (!dbInstance) throw new Error('Database not initialized. Call initDatabase() first.');
  return dbInstance;
}

/** Persist immediately. */
export async function persistNow(): Promise<void> {
  if (!dbInstance) return;
  const data = dbInstance.export();
  await backend().save(data);
}

/** Debounced persistence so bursts of writes coalesce into one storage write. */
export function schedulePersist(): void {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    void persistNow();
  }, 250);
}

/** Desktop only: write a dated backup file (keep the most recent ones). */
export async function backupNow(): Promise<void> {
  if (!isTauri() || !dbInstance) return;
  try {
    await invoke('db_backup', { dataB64: u8ToB64(dbInstance.export()) });
  } catch {
    // Backups are best-effort; the primary save already succeeded.
  }
}

export async function resetDatabase(): Promise<void> {
  await backend().clear();
  // Also clear any legacy IndexedDB copy on desktop.
  if (isTauri()) await idbClear().catch(() => {});
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
  notifyDbChanged();
}

export async function exportDatabaseBytes(): Promise<Uint8Array> {
  await persistNow();
  return getDb().export();
}

/**
 * Import a backup. The new bytes are opened and validated FIRST — the live
 * database is only swapped once the import is known-good, so a wrong file
 * leaves existing data untouched.
 */
export async function importDatabaseBytes(bytes: Uint8Array): Promise<void> {
  const SQL = await getSqlModule();
  let candidate: SqlJsDatabase | null = null;
  try {
    candidate = new SQL.Database(bytes);
    const version = getSchemaVersion(candidate); // throws "no such table" on non-app files
    if (version === null) {
      throw new Error('the file has no schema version — it is not a Dental Interview Coach backup.');
    }
    if (version > SCHEMA_VERSION) {
      throw new Error(
        `the backup is from a newer app version (schema v${version}; this app supports v${SCHEMA_VERSION}).`,
      );
    }
    candidate.run(SCHEMA_SQL);
    runMigrations(candidate);
    // Sanity check a core table is readable.
    candidate.exec('SELECT COUNT(*) FROM interview_question');
  } catch (e) {
    candidate?.close();
    throw new Error(`Import rejected — your existing data is untouched. (${(e as Error).message})`);
  }

  const old = dbInstance;
  dbInstance = candidate;
  old?.close();
  await persistNow();
  notifyDbChanged();
}

// --- Query helpers ---------------------------------------------------------

export type Row = Record<string, SqlValue>;

/** Run a SELECT and return typed rows. */
export function query(sql: string, params: SqlValue[] = []): Row[] {
  const db = getDb();
  const stmt = db.prepare(sql);
  try {
    stmt.bind(params);
    const rows: Row[] = [];
    while (stmt.step()) {
      rows.push(stmt.getAsObject());
    }
    return rows;
  } finally {
    stmt.free();
  }
}

export function queryOne(sql: string, params: SqlValue[] = []): Row | null {
  const rows = query(sql, params);
  return rows.length ? rows[0] : null;
}

/** Run an INSERT/UPDATE/DELETE, schedule persistence, and notify subscribers. */
export function execute(sql: string, params: SqlValue[] = []): void {
  const db = getDb();
  db.run(sql, params);
  schedulePersist();
  notifyDbChanged();
}
